// Binary wire codec for the two high-frequency messages — client input and
// server snapshots. These dominate bandwidth, so they're packed into bytes
// instead of JSON. Everything else (lobby/config/welcome/…) stays JSON.
//
// Frame discrimination: text frames are JSON; binary frames are these. The
// first byte is a message tag.

import { POWERUP_TYPES, type FlagState, type PowerupType } from "../protocol/index.js";

export const MSG_INPUT = 1;
export const MSG_SNAPSHOT = 2;
export const MSG_SNAPSHOT_SLIM = 3;

// --- enum <-> code tables (derived from the power-up registry) -------------

// Pickup codes: every power-up in registry order.
export const PUP_CODES: PowerupType[] = POWERUP_TYPES;
// Flag-state codes.
export const FLAG_STATES: FlagState[] = ["home", "carried", "dropped", "held"];
export const flagStateCode = (s: FlagState): number => Math.max(0, FLAG_STATES.indexOf(s));
export const pupCode = (p: PowerupType): number => Math.max(0, PUP_CODES.indexOf(p));

// Composable bullet effect flags packed into one byte.
export const BF_HOMING = 1;
export const BF_EXPLOSIVE = 2;
export const BF_PIERCE = 4;
export const bulletFlags = (b: { homing: boolean; explosive: boolean; pierce: boolean }): number =>
  (b.homing ? BF_HOMING : 0) | (b.explosive ? BF_EXPLOSIVE : 0) | (b.pierce ? BF_PIERCE : 0);

// --- quantization helpers --------------------------------------------------

const A = 32767 / Math.PI; // angle (radians) <-> int16
const TAU = Math.PI * 2;
// bodyAngle accumulates unbounded on the server, so wrap into [-π, π] before
// quantizing — otherwise large angles saturate the int16 and the body freezes.
const wrap = (rad: number): number => {
  let a = rad % TAU;
  if (a > Math.PI) a -= TAU;
  else if (a < -Math.PI) a += TAU;
  return a;
};
export const encAngle = (rad: number): number =>
  Math.max(-32767, Math.min(32767, Math.round(wrap(rad) * A)));
export const decAngle = (v: number): number => v / A;
export const u16 = (n: number): number => Math.max(0, Math.min(65535, Math.round(n)));
export const ds = (sec: number): number => Math.max(0, Math.min(255, Math.round(sec * 10))); // deciseconds
export const decDs = (v: number): number => v / 10;

/** Byte-equality check for snapshot change-gating. */
export function bytesEqual(a: Uint8Array | null, b: Uint8Array): boolean {
  if (!a || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
